import React, { useEffect, useState } from "react";
import { slipPdfToJpeg } from "@/lib/pdfToJpeg";
import { rupiah } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Share2, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const waDigits = (n) => {
  let d = String(n || "").replace(/[^0-9]/g, "");
  if (!d) return "";
  if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (!d.startsWith("62")) d = "62" + d;
  return d;
};

// Bagikan slip gaji beberapa karyawan sekaligus (JPEG Full HD).
export default function SlipBulkShare({ open, onClose, details, period, companyName, empMap }) {
  const [files, setFiles] = useState({});
  const [progress, setProgress] = useState(0);
  const [sent, setSent] = useState({});

  const fname = (d) => `slip_${d.employee_nama}_${period?.nama || ""}`.replace(/\s+/g, "_") + ".jpg";
  const text = (d) => `*Slip Gaji ${companyName}*\n${d.employee_nama}\nPeriode: ${period?.nama || "-"}\nGaji Bersih (THP): ${rupiah(d.gaji_bersih || 0)}\n\nTerlampir slip gaji Anda. Terima kasih.`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFiles({}); setProgress(0); setSent({});
    (async () => {
      for (let i = 0; i < details.length; i++) {
        const d = details[i];
        try {
          const { blob } = await slipPdfToJpeg(`/payroll/details/${d.id}/slip.pdf`);
          if (cancelled) return;
          setFiles((f) => ({ ...f, [d.id]: new File([blob], fname(d), { type: "image/jpeg" }) }));
        } catch (_) { /* skip */ }
        if (!cancelled) setProgress(i + 1);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open]);

  const ready = progress >= details.length;
  const all = details.map((d) => files[d.id]).filter(Boolean);
  const dl = (f) => { const u = URL.createObjectURL(f); const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); URL.revokeObjectURL(u); };

  const shareAll = async () => {
    if (navigator.canShare && navigator.canShare({ files: all })) {
      try { await navigator.share({ files: all, title: `Slip Gaji ${period?.nama || ""}`, text: `Slip gaji ${all.length} karyawan — ${period?.nama || ""}` }); return; }
      catch (e) { if (e?.name === "AbortError") return; }
    }
    all.forEach((f, i) => setTimeout(() => dl(f), i * 300));
    toast.info("Perangkat ini tidak mendukung bagikan banyak file — semua JPEG diunduh.", { duration: 6000 });
  };

  const sendOne = (d) => {
    const f = files[d.id];
    const digits = waDigits(empMap[d.employee_id]);
    if (f) dl(f);
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text(d))}`, "_blank", "noopener");
    setSent((s) => ({ ...s, [d.id]: true }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="slip-bulk-dialog">
        <DialogHeader><DialogTitle>Bagikan Slip Terpilih ({details.length})</DialogTitle>
          <DialogDescription>Slip diubah ke JPEG Full HD. Bagikan sekaligus (mis. ke grup WA) atau kirim satu per satu ke nomor masing-masing.</DialogDescription>
        </DialogHeader>
        {!ready && <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="slip-bulk-progress"><Loader2 className="h-4 w-4 animate-spin" /> Menyiapkan {progress}/{details.length} slip…</div>}
        <div className="flex flex-wrap gap-2">
          <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" disabled={!ready || !all.length} onClick={shareAll} data-testid="slip-bulk-share-all"><Share2 className="h-4 w-4 mr-1" /> Bagikan Semua Sekaligus</Button>
          <Button variant="outline" disabled={!ready || !all.length} onClick={() => all.forEach((f, i) => setTimeout(() => dl(f), i * 300))} data-testid="slip-bulk-download"><Download className="h-4 w-4 mr-1" /> Unduh Semua JPEG</Button>
        </div>
        <div className="divide-y rounded-xl border border-slate-200">
          {details.map((d, i) => {
            const digits = waDigits(empMap[d.employee_id]);
            return (
              <div key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`slip-bulk-row-${i}`}>
                <div className="flex-1"><div className="font-semibold text-slate-800">{d.employee_nama}</div>
                  <div className="text-xs text-slate-500">{rupiah(d.gaji_bersih)} · {digits ? `+${digits}` : <span className="text-amber-600">No. WA belum diisi</span>}</div></div>
                {sent[d.id] && <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid={`slip-bulk-sent-${i}`} />}
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" disabled={!files[d.id]} onClick={() => sendOne(d)} data-testid={`slip-bulk-send-${i}`}>
                  {files[d.id] ? <MessageCircle className="h-4 w-4 mr-1" /> : <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Kirim WA
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
